import {
  isObject,
  get,
  set,
  isEmpty,
  keys,
  isString,
  isArray,
  cloneDeep,
} from 'lodash'

import { produce } from 'immer'
import { appLanguages, questionType } from '@edulastic/constants'
import { LANGUAGES_OPTIONS } from '@edulastic/constants/const/languages'
import { PassageDefaultTemplate } from '@edulastic/constants/const/questionType'
import { stackedTranslationEnabledQuestionTypes } from '../constants/constantsForQuestions'

const { useLanguageFeatureQn } = questionType
const { LANGUAGE_EN } = appLanguages

const commonPatterns = [
  /scoringInstructions/,
  /instructorStimulus/,
  /sampleAnswer/,
  /stimulus/,
  /hints\.(\d+)\.label/,
  /tts\.taskStatus/,
  /tts\.taskId/,
  /tts\.titleAudioURL/,
  /tts\.ttsCreatedAt/,
]

export const patternsByQuestionType = {
  [questionType.CLOZE_DRAG_DROP]: [...commonPatterns, /options\.(\d+)\.label/],
  [questionType.CLOZE_IMAGE_DRAG_DROP]: [
    ...commonPatterns,
    /options\.(\d+)\.value/,
  ],
  [questionType.EXPRESSION_MULTIPART]: [
    ...commonPatterns,
    /options\.(.*?)\.(\d+)/,
    /validation\.validResponse\.dropdown\.value\.(\d+)\.value/,
    /validation\.validResponse\.textinput\.value\.(\d+)\.value/,
    /validation\.validResponse\.mathUnits\.value\.(\d+)\.value/,
    /validation\.validResponse\.value\.(\d+)\.(\d+)\.value/,
    /validation\.altResponses\.(\d+)\.dropdown\.value\.(\d+)\.value/,
    /validation\.altResponses\.(\d+)\.textinput\.value\.(\d+)\.value/,
    /validation\.altResponses\.(\d+)\.mathUnits\.value\.(\d+)\.value/,
    /validation\.altResponses\.(\d+)\.value\.(\d+)\.(\d+)\.value/,
  ],
  [questionType.CHOICE_MATRIX]: [
    ...commonPatterns,
    /options\.(\d)/,
    /stems\.(.*\d+)/,
    /uiStyle\.optionRowTitle/,
    /uiStyle\.stemTitle/,
  ],
  [questionType.CLOZE_DROP_DOWN]: [
    ...commonPatterns,
    /options\.(.*?)\.(\d+)/,
    /validation\.validResponse\.value\.(\d+)\.value/,
    /validation\.validResponse\.value\.(\d+)\.index/,
    /validation\.validResponse\.value\.(\d+)\.id/,
    /validation\.altResponses\.(\d+)\.value\.(\d+)\.value/,
    /validation\.altResponses\.(\d+)\.value\.(\d+)\.index/,
    /validation\.altResponses\.(\d+)\.value\.(\d+)\.id/,
  ],
  [questionType.CLOZE_IMAGE_TEXT]: [
    ...commonPatterns,
    /validation\.validResponse\.value\.(.*?)/,
    /validation\.altResponses\.(\d+)\.value\.(.*?)/,
  ],
  [questionType.CLOZE_TEXT]: [
    ...commonPatterns,
    /options\.(.*?)\.(\d+)/,
    /validation\.validResponse\.value\.(\d+)\.value/,
    /validation\.altResponses\.(\d+)\.value\.(\d+)\.value/,
  ],
  [questionType.MATH]: [...commonPatterns],
  [questionType.FORMULA_ESSAY]: [...commonPatterns],
  [questionType.MULTIPLE_CHOICE]: [...commonPatterns, /options\.(\d+)\.label/],
  [questionType.TOKEN_HIGHLIGHT]: [...commonPatterns, /template/],
  [questionType.TEXT]: [...commonPatterns, /heading/, /content/],
  [questionType.VIDEO]: [
    ...commonPatterns,
    /sourceURL/,
    /videoType/,
    /heading/,
    /summary/,
    /transcript/,
  ],
  [questionType.PASSAGE]: [
    ...commonPatterns,
    /heading/,
    /contentsTitle/,
    /content/,
    /pages\.(\d+)/,
  ],
  [questionType.CLASSIFICATION]: [
    ...commonPatterns,
    /uiStyle\.rowTitles\.(\d+)/,
    /uiStyle\.columnTitles\.(\d+)/,
    /possibleResponses\.(\d+)\.value/,
    /possibleResponseGroups\.(\d+)\.title/,
    /possibleResponseGroups\.(\d+)\.responses\.(\d+).value/,
  ],
  [questionType.ORDER_LIST]: [...commonPatterns, /list\.(.*?)/],
  [questionType.CLOZE_IMAGE_DROP_DOWN]: [
    ...commonPatterns,
    /options\.(.*?)\.(\d+)/,
    /validation\.validResponse\.value\.(.*?)/,
    /validation\.altResponses\.(\d+)\.value\.(.*?)/,
  ],
  [questionType.ESSAY_RICH_TEXT]: [
    ...commonPatterns,
    /placeholder/,
    /metadata\.distractor_rationale_response_level\.(\d+)/,
  ],
  [questionType.ESSAY_PLAIN_TEXT]: [
    ...commonPatterns,
    /placeholder/,
    /metadata\.distractor_rationale_response_level\.(\d+)/,
  ],
  // [questionType.SHORT_TEXT]: [
  //   ...commonPatterns,
  //   /placeholder/,
  //   /metadata\.distractor_rationale_response_level\.(\d+)/,
  //   /validation\.validResponse\.value/,
  //   /validation\.altResponses\.(\d+)\.value/,
  // ],
}

export const clozeTypes = [
  questionType.CLOZE_DRAG_DROP,
  questionType.EXPRESSION_MULTIPART,
  questionType.CLOZE_DROP_DOWN,
  questionType.CLOZE_TEXT,
  questionType.CLOZE_IMAGE_DRAG_DROP,
  questionType.CLOZE_IMAGE_DROP_DOWN,
  questionType.CLOZE_IMAGE_TEXT,
]

export const isEnglishContentEmpty = (data) => {
  const defaultContent = PassageDefaultTemplate.content
  const defaultTitle = PassageDefaultTemplate.title
  if (
    data?.stimulus ||
    (data?.contentsTitle && data?.contentsTitle !== defaultTitle) ||
    data?.summary ||
    (data?.content &&
      (data?.content !== defaultContent || data.type === 'text')) ||
    data?.sourceURL
  ) {
    return false
  }
  return true
}

/**
 * Get all available paths in the data object, recursively.
 */
const getAvailablePaths = (data, prev = '', paths = []) => {
  Object.keys(data).forEach((key) => {
    const path = prev + (prev ? '.' : '') + key
    if (isObject(data[key]) && key !== 'languageFeatures') {
      return getAvailablePaths(data[key], path, paths)
    }
    if (key !== 'languageFeatures') {
      paths.push(path)
    }
  })
  return paths
}

/**
 * Get all available paths in the data object, recursively.
 * Here we are using the patternsByQuestionType object to get the paths
 */
export const getLanguageDataPaths = (patterns, data) => {
  const availablePaths = getAvailablePaths(data)
  return availablePaths.filter((path) =>
    patterns?.some((pattern) => new RegExp(pattern, 'g').test(path))
  )
}

const findRemovedIndex = (newOptions = [], prevOptions = []) => {
  let removedIndex = -1
  const newValues = newOptions.map((op) => {
    if (isString(op)) {
      return op
    }
    return op.value
  })
  prevOptions.forEach((op, opIndex) => {
    const compare = isString(op) ? op : op.value
    if (!newValues.includes(compare)) {
      removedIndex = opIndex
    }
  })
  return removedIndex
}

export const changeDataInPreferredLanguage = (
  language,
  prevQuestion,
  newQuestion,
  allowedToSelectMultiLanguage
) => {
  if (
    language &&
    language !== LANGUAGE_EN &&
    useLanguageFeatureQn.includes(newQuestion.type) &&
    patternsByQuestionType[newQuestion.type]
  ) {
    const patterns = patternsByQuestionType[newQuestion.type]
    const dataFields = getLanguageDataPaths(patterns, newQuestion)
    const changedQuestion = produce(newQuestion, (draft) => {
      if (!draft.languageFeatures) {
        draft.languageFeatures = {}
      }

      if (!draft.languageFeatures[language]) {
        draft.languageFeatures[language] = {}
      }

      dataFields.forEach((path) => {
        const currentData = get(newQuestion, path)
        // don't update empty stimulus to other languages
        if (path !== 'stimulus' || currentData) {
          set(draft.languageFeatures[language], path, currentData)
        }
        // use previous data for English
        // if it exist in previouse data.
        const prevData = get(prevQuestion, path)
        if (prevData) {
          set(draft, path, prevData)
        } else {
          set(draft, path, '')
        }
        if (questionType.TOKEN_HIGHLIGHT === newQuestion.type) {
          draft.languageFeatures[language].validation = newQuestion.validation
          draft.languageFeatures[language].templeWithTokens =
            newQuestion.templeWithTokens

          draft.validation = prevQuestion.validation
          draft.templeWithTokens = prevQuestion.templeWithTokens
        }
      })
    })

    return changedQuestion
  }

  // I clean existing languageData when EN data is updated
  if (
    (!language || language === LANGUAGE_EN) &&
    useLanguageFeatureQn.includes(newQuestion.type) &&
    newQuestion.languageFeatures &&
    !allowedToSelectMultiLanguage
  ) {
    const changedData = produce(newQuestion, (draft) => {
      keys(draft.languageFeatures).forEach((langKey) => {
        const langDataPaths = getAvailablePaths(draft.languageFeatures[langKey])

        if (newQuestion.type === questionType.MULTIPLE_CHOICE) {
          const removedIndex = findRemovedIndex(
            newQuestion?.options,
            prevQuestion?.options
          )
          if (removedIndex !== -1) {
            draft.languageFeatures[langKey]?.options?.splice(removedIndex, 1)
          }
        } else if (newQuestion.type === questionType.CHOICE_MATRIX) {
          const removedStemIndex = findRemovedIndex(
            newQuestion?.stems,
            prevQuestion?.stems
          )
          if (removedStemIndex !== -1) {
            draft.languageFeatures[langKey]?.stems?.splice(removedStemIndex, 1)
          }
          const removedOpIndex = findRemovedIndex(
            newQuestion?.options,
            prevQuestion?.options
          )
          if (removedOpIndex !== -1) {
            draft.languageFeatures[langKey]?.options?.splice(removedOpIndex, 1)
          }
        } else if (newQuestion.type === questionType.CLOZE_DROP_DOWN) {
          // assume responseIds are same here
          const { responseIds, options: newOptions } = newQuestion
          const { options: prevOptions } = prevQuestion
          responseIds.forEach((response) => {
            const { id } = response
            if (newOptions[id]?.length !== prevOptions[id]?.length) {
              const removedIndex = findRemovedIndex(
                newOptions[id],
                prevOptions[id]
              )
              if (removedIndex !== -1) {
                draft.languageFeatures?.[langKey]?.options?.[id]?.splice(
                  removedIndex,
                  1
                )
              }
            }
          })
        } else if (newQuestion.type === questionType.EXPRESSION_MULTIPART) {
          const { options: newOptions, responseIds } = newQuestion
          const { options: prevOptions } = prevQuestion
          const { dropDowns } = responseIds
          if (isArray(dropDowns)) {
            dropDowns.forEach((dropdown) => {
              const removedIndex = findRemovedIndex(
                newOptions[dropdown.id],
                prevOptions[dropdown.id]
              )
              if (removedIndex !== -1) {
                draft.languageFeatures?.[langKey]?.options?.[
                  dropdown.id
                ]?.splice(removedIndex, 1)
              }
            })
          }
        }

        const cleanLangData = {}
        langDataPaths.forEach((path) => {
          const enData = get(draft, path)
          const langData = get(draft.languageFeatures[langKey], path)
          /**
           * @see https://goguardian.atlassian.net/browse/EV-36104
           * For token highlight qType data can differ for "validation.validResponse.value" or "templeWithTokens" paths
           * as data can be different for different languages. Thus need not compare enData and langData for setting
           * other language "validation" and "templateWithTokens"
           */
          if (
            newQuestion.type === questionType.TOKEN_HIGHLIGHT &&
            langData &&
            typeof path === 'string' &&
            (path.startsWith('validation.validResponse.value') ||
              path.startsWith('templeWithTokens'))
          ) {
            set(cleanLangData, path, langData)
          } else if (enData && langData) {
            set(cleanLangData, path, langData)
          }
        })

        draft.languageFeatures[langKey] = cleanLangData
      })
    })
    return changedData
  }

  return newQuestion
}

export const changeDataToPreferredLanguage = (
  questionData,
  language,
  view = '',
  addOriginalQuestion = false
) => {
  /** Avoid overriding question when the method called more than once */
  const { stimulus, options = [], tts, languageFeatures } =
    questionData?.originalQuestion || questionData || {}
  const originalQuestion = {
    ...(stimulus ? { stimulus } : {}),
    ...(options?.length ? { options } : {}),
    ...(tts ? { tts } : {}),
    ...(languageFeatures ? { languageFeatures } : {}),
  }

  if (
    view === 'preview' &&
    isEnglishContentEmpty(questionData) &&
    isEnglishContentEmpty(questionData?.languageFeatures?.[language]) &&
    questionData?.languageFeatures
  ) {
    // Changing english language to first languageFeatures language only when view is preview
    // Extracting the first languageCode from keys of languageFeatures
    // Loop through the language codes and get the stimulus of the first language that is not empty
    const languageCodes = LANGUAGES_OPTIONS.map((lang) => lang.value)
    for (const languageCode of languageCodes) {
      const languageStimulus =
        languageFeatures?.[languageCode]?.stimulus ||
        languageFeatures?.[languageCode]?.contentsTitle ||
        languageFeatures?.[languageCode]?.content ||
        languageFeatures?.[languageCode]?.sourceURL
      // If the stimulus is not empty, then set the language and break the loop
      if (languageStimulus) {
        language = languageCode
        break
      }
    }
  }
  if (
    LANGUAGE_EN !== language &&
    useLanguageFeatureQn.includes(questionData?.type) &&
    questionData?.languageFeatures?.[language]
  ) {
    const languageData = questionData.languageFeatures[language]
    const languageDataPaths = getAvailablePaths(languageData)

    const changedData = produce(questionData, (draft) => {
      languageDataPaths.forEach((path) => {
        const langData = get(languageData, path)
        set(draft, path, langData)

        if (questionType.TOKEN_HIGHLIGHT === questionData.type) {
          draft.validation = languageData.validation
          draft.templeWithTokens = languageData.templeWithTokens
        }

        if (
          stackedTranslationEnabledQuestionTypes.includes(questionData?.type) &&
          !isEmpty(originalQuestion)
        ) {
          draft.originalQuestion = originalQuestion
        }
        if (!addOriginalQuestion) {
          delete draft.originalQuestion
        }
      })
    })

    return changedData
  }

  if (!isEmpty(questionData) && !addOriginalQuestion) {
    return produce(questionData, (draft) => {
      delete draft.originalQuestion
    })
  }

  return questionData
}

export const resetEmptyLanguageData = (data, language) => {
  const languageData = get(data, ['languageFeatures', language], {})
  let previousData = data
  if (
    !isEmpty(language) &&
    isEmpty(languageData) &&
    !isEmpty(data?.languageFeatures) &&
    isEnglishContentEmpty(data)
  ) {
    const languageCodes = Object.keys(data.languageFeatures)
    const sortLanguageCodes = LANGUAGES_OPTIONS.filter((lang) =>
      languageCodes.includes(lang.value)
    )
    const firstLanguage = sortLanguageCodes[0].value
    if (firstLanguage !== language) {
      previousData = data.languageFeatures[firstLanguage]
    }
    const languageDataPaths = getAvailablePaths(previousData)
    const changedQuestion = produce(data, (draft) => {
      languageDataPaths.forEach((path) => {
        const langData = get(languageData, path)
        const endData = get(previousData, path)
        if (!langData) {
          set(draft, `languageFeatures.${language}.${path}`, endData)
        }
      })
    })
    return changedQuestion
  }
  return null
}

export const resetEmptyEnglishData = (data) => {
  const clonedQuestion = cloneDeep(data)
  if (!isEmpty(data?.languageFeatures) && isEnglishContentEmpty(data)) {
    const languageCodes = Object.keys(data.languageFeatures)
    const sortLanguageCodes = LANGUAGES_OPTIONS.filter((lang) =>
      languageCodes.includes(lang.value)
    )

    const languageDataWithStimulus = sortLanguageCodes
      .map(({ value }) => value)
      .map((languageCode) => clonedQuestion.languageFeatures[languageCode])
      .find((languageContent) => languageContent?.stimulus?.length)

    if (languageDataWithStimulus) {
      const changedQuestion = produce(data, (draft) => {
        const languageDataPaths = getAvailablePaths(languageDataWithStimulus)
        languageDataPaths.forEach((path) => {
          const langData = get(draft, path)
          const endData = get(languageDataWithStimulus, path)
          if (!langData) {
            set(draft, path, endData)
          }
        })
      })
      return changedQuestion
    }
    return null
  }
  return null
}

// return a new version of the item (newItem)
// where data related to languages not present in langauageTabs has been removed or cleared.
// It also returns a flag (omitted) indicating whether any data was actually removed.
export const omitEmptyItemData = (item, langauageTabs) => {
  const newItem = { ...item }
  let omitted = false
  if (!isEnglishContentEmpty(newItem) && !langauageTabs.includes(LANGUAGE_EN)) {
    const patterns = patternsByQuestionType[newItem.type]
    const dataFields = getLanguageDataPaths(patterns, newItem)
    dataFields.forEach((path) => {
      set(newItem, path, '')
    })

    omitted = true
  }
  if (newItem.languageFeatures) {
    newItem.languageFeatures = Object.keys(newItem.languageFeatures).reduce(
      (acc, key) => {
        if (langauageTabs.includes(key)) {
          acc[key] = newItem.languageFeatures[key]
        } else {
          omitted = true
        }
        return acc
      },
      {}
    )
  }
  return { newItem, omitted }
}

export const getPrimaryLanguageCode = ({
  stimulus,
  contentsTitle,
  content,
  sourceURL,
  languageFeatures,
} = {}) => {
  if (stimulus || contentsTitle || content || sourceURL) {
    return LANGUAGE_EN
  }

  let primaryLanguageCode = LANGUAGE_EN
  const languageCodes = LANGUAGES_OPTIONS.map((lang) => lang.value)
  for (const languageCode of languageCodes) {
    const languageStimulus =
      languageFeatures?.[languageCode]?.stimulus ||
      languageFeatures?.[languageCode]?.contentsTitle ||
      languageFeatures?.[languageCode]?.content ||
      languageFeatures?.[languageCode]?.sourceURL
    // If the stimulus is not empty, then set the language and break the loop
    if (languageStimulus) {
      primaryLanguageCode = languageCode
      break
    }
  }

  return primaryLanguageCode
}

export const initializeLanguageObj = (draft, language, initialValues) => {
  if (!draft.languageFeatures) {
    draft.languageFeatures = {}
  }

  if (!draft.languageFeatures[language]) {
    draft.languageFeatures[language] = initialValues
  }

  if (
    !draft.languageFeatures[language].responseIds &&
    initialValues.responseIds
  ) {
    draft.languageFeatures[language].responseIds = initialValues.responseIds
  }

  if (!draft.languageFeatures[language].options && initialValues.options) {
    draft.languageFeatures[language].options = initialValues.options
  }
  if (
    !draft.languageFeatures[language].validation &&
    initialValues.validation
  ) {
    draft.languageFeatures[language].validation = initialValues.validation
  }
  if (!draft.languageFeatures[language].extraOpts && initialValues.extraOpts) {
    draft.languageFeatures[language].extraOpts = initialValues.extraOpts
  }
}
